<script setup lang="ts">
import {
	computed,
	nextTick,
	onBeforeMount,
	onBeforeUnmount,
	onMounted,
	Ref,
	ref,
	toRefs,
	watch,
} from "vue";
import { useI18n } from "vue-i18n";

import SelectionItem from "@/modules/explorer/components/AxisConfiguration/SelectionItem.vue";

import {
	usePropertyStore,
	verticalHasSegmentOption,
} from "@/modules/explorer/PropertyStore";
import CheckboxSelectionItem from "@/modules/explorer/components/AxisConfiguration/CheckboxSelectionItem.vue";
import DataTableDO = App.Data.Explorer.DataTableDO;
import DocumentDO = App.Data.Explorer.DocumentDO;
const { t } = useI18n();

import SelectDimensionSpecification from "@/modules/explorer/components/AxisConfiguration/SelectDimensionSpecification.vue";
import { familyToOpen } from "@/modules/explorer/components/AxisConfiguration/familyToOpen";
import { ucfirst } from "chartjs-graph";
import DimensionDO = App.Data.DimensionDO;

export type LocalizedVerticalDO = App.Data.DocumentVerticalDTO & {
	localizedTitle: string;
	localizedDescription: string;
};
export type LocalizedDimensionDO = App.Data.DocumentDimensionDTO & {
	localizedTitle: string;
	localizedDescription: string;
};
export type AxisItem = LocalizedVerticalDO | LocalizedDimensionDO;

const filterInput = defineModel<string>("search", { default: "" });

const props = withDefaults(
	defineProps<{
		axisNameClean: string;
		items: AxisItem[];
		modelValue: any | null;
		selectMode?: string;
		nullableAxis?: string[];
		dataTable?: DataTableDO | null;
		document?: DocumentDO | null;
		hasMultipleSelected?: boolean;
	}>(),
	{
		modelValue: null,
		selectMode: "single",
		nullableAxis: () => ["secondary-vertical", "offset", "legend"],
		dataTable: null,
		document: null,
		hasMultipleSelected: false,
	}
);

const emit = defineEmits(["update:modelValue"]);

const { items, axisNameClean, nullableAxis, document } = toRefs(props);
const scrollContent = ref(null);
const searchField = ref();
const selectionRoot = ref<HTMLElement | null>(null);
const familyColumn = ref<HTMLElement | null>(null);

// Used to v-model the selection for a Vertical or Metric
const activeDomain = ref();

const activeVerticalDomains = computed(() => {
	if (props.dataTable) {
		return [
			...new Set(props.dataTable.metrics.map((metric) => metric.domain).flat()),
		];
	}
	return [
		document.value.vertical.domain,
		document.value?.secondary_vertical?.domain,
	]
		.flat()
		.filter(Boolean)
		.filter((value, index, self) => self.indexOf(value) === index);
});

const isVertical = computed(() => {
	return ["vertical", "secondary-vertical", "metrics"].includes(
		axisNameClean.value
	);
});

// A value-based horizontal sort overrides the alphabetical sort order, so inverting it has no
// effect. Disable the toggle in that case so it doesn't appear active while doing nothing.
const isSortDirectionDisabled = computed(() => {
	return (
		axisNameClean.value === "horizontal" &&
		!!document.value?.is_horizontal_sorting_enabled
	);
});

const documentAttribute = computed({
	get() {
		return props.modelValue;
	},
	set(value) {
		emit("update:modelValue", value);
	},
});

const propertyStore = usePropertyStore();

const propertyIsAvailable = (item: AxisItem): boolean => {
	if (props.dataTable) {
		return propertyStore.isAvailableForDataTable(props.dataTable, item);
	}

	return (
		propertyStore.isAvailable(item, null, axisNameClean.value) &&
		(axisNameClean.value !== "legend" ||
			propertyStore.isAvailableAsLegend(item as DimensionDO, document.value))
	);
};

const itemSubSet = computed(() => {
	// filter items the correct way for verticals/metrics selection and for dimensions

	const domainItems = isVertical.value
		? items.value.filter((item) => item.domain.includes(activeDomain.value))
		: items.value.filter((item) =>
				activeVerticalDomains.value.some((value) => item.domain.includes(value))
		  );

	// Hide families in which nothing is selectable, unless they hold the current selection.
	const selectedFamilies = [documentAttribute.value ?? []]
		.flat()
		.map((selected: AxisItem) => selected.family_name_clean);
	const keptFamilies = new Set(
		domainItems
			.filter(
				(item) =>
					selectedFamilies.includes(item.family_name_clean) ||
					propertyIsAvailable(item)
			)
			.map((item) => item.family_name_clean)
	);

	return domainItems.filter((item) => keptFamilies.has(item.family_name_clean));
});

const itemFamilies = computed(() => [
	...new Set(itemSubSet.value.map((item) => item.family_name_clean)),
]);

const itemDomains = computed(() => {
	return [...new Set(items.value.flatMap((item) => item.domain))];
});

// open in interface
const activeFamily: Ref<string | null> = ref(null);

const openFamilyOfSelection = () => {
	activeFamily.value = familyToOpen(
		documentAttribute.value,
		itemFamilies.value
	);
};

const familyTabsKey = computed(() => itemFamilies.value.join("|"));

watch(activeDomain, () => {
	// Families are domain specific, so keep the open tab if it still exists.
	if (activeFamily.value && itemFamilies.value.includes(activeFamily.value)) {
		return;
	}

	openFamilyOfSelection();
});

// has active item
const familyWithActiveItem = computed(() => {
	return documentAttribute.value?.family_name_clean ?? null;
});

const isSearching = computed(() => filterInput.value.length > 0);

type VisibleItems = Record<string, AxisItem[]>;

const groupedVisibleItems = computed<VisibleItems>(() => {
	if (filterInput.value !== "") {
		return Object.groupBy(
			[
				...itemSubSet.value.filter(
					(item) =>
						item.localizedTitle
							.toLowerCase()
							.includes(filterInput.value.toLowerCase()) ||
						item.localizedDescription
							.toLowerCase()
							.includes(filterInput.value.toLowerCase()) ||
						item.name_clean
							.toLowerCase()
							.includes(filterInput.value.toLowerCase())
				),
			],
			(item: AxisItem) => item.family_name_clean
		);
	} else {
		return {
			"": [
				...itemSubSet.value.filter(
					(item) => item.family_name_clean === activeFamily.value
				),
			],
		};
	}
});

watch(groupedVisibleItems, () => {
	// resets scroll position and keyboard highlight when changing tab or filtering.
	highlightedIndex.value = -1;
	if (scrollContent.value) {
		scrollContent.value.$el.scrollTop = 0;
	}
});

const resetAxis = () => {
	if (nullableAxis.value.includes(axisNameClean.value)) {
		documentAttribute.value = null;
	}
};

const itemsWithFamily = (itemFamily: string): number => {
	return documentAttribute.value.filter(
		(selection) => selection.family_name_clean === itemFamily
	).length;
};

onBeforeMount(() => {
	// Domain first: it decides which families exist.
	setActiveDomain();
	openFamilyOfSelection();
});

watch(axisNameClean, () => {
	setActiveDomain();
	openFamilyOfSelection();
});

const setActiveDomain = () => {
	// when switching back to / opening a Metric selection we need to set the domain
	if (axisNameClean.value === "vertical") {
		activeDomain.value = document.value.vertical.domain[0];
	}
	if (axisNameClean.value === "secondary-vertical") {
		activeDomain.value =
			document.value?.secondary_vertical?.domain[0] ?? itemDomains.value[0];
	}
	if (axisNameClean.value === "metrics") {
		activeDomain.value =
			props.dataTable?.metrics?.length > 0
				? props.dataTable.metrics[0].domain[0]
				: itemDomains.value[0];
	}
};

const updateDocumentAttribute = (item: AxisItem) => {
	if (!Array.isArray(documentAttribute.value)) {
		documentAttribute.value = item;

		return;
	}

	const index = documentAttribute.value.findIndex((x) => x.id === item.id);
	if (index !== -1) {
		documentAttribute.value.splice(index, 1, item);
	}
};

// Keyboard navigation for the panel:
// - Left/Right move between columns (families / items / remove button).
// - Up/Down navigate within the active column: cycling family tabs, or moving a
//   highlight over the visible items (in render order, across groups).
// - Enter selects the highlighted item, drills from families into items, or
//   triggers the remove button depending on the active column.
const flatVisibleItems = computed<AxisItem[]>(() =>
	Object.values(groupedVisibleItems.value).flat()
);

const highlightedIndex = ref(-1);

const highlightedItemId = computed<number | string | null>(
	() => flatVisibleItems.value[highlightedIndex.value]?.id ?? null
);

const scrollHighlightedIntoView = () => {
	nextTick(() => {
		if (highlightedItemId.value == null) {
			return;
		}
		const element = scrollContent.value?.$el.querySelector(
			`[data-axis-item="${CSS.escape(String(highlightedItemId.value))}"]`
		);
		element?.scrollIntoView({ block: "nearest" });
	});
};

// Move the item highlight up/down. -1 means "above the first item", i.e. the
// search bar at the top of the items column (no item highlighted).
const moveHighlight = (direction: 1 | -1) => {
	const visibleItems = flatVisibleItems.value;
	if (visibleItems.length === 0) {
		return;
	}

	// Skip unavailable items so Enter always lands on something selectable.
	let nextIndex = highlightedIndex.value;
	for (let i = 0; i < visibleItems.length; i++) {
		nextIndex += direction;
		if (nextIndex < 0) {
			// Moved above the first item: back up to the search bar.
			highlightedIndex.value = -1;
			return;
		}
		if (nextIndex > visibleItems.length - 1) {
			return;
		}
		if (propertyIsAvailable(visibleItems[nextIndex])) {
			highlightedIndex.value = nextIndex;
			scrollHighlightedIntoView();
			return;
		}
	}
};

const moveHighlightedFamily = (direction: 1 | -1) => {
	const families = itemFamilies.value;
	if (families.length === 0) {
		return;
	}

	const currentIndex = families.indexOf(activeFamily.value ?? "");
	const nextIndex = Math.min(
		Math.max(currentIndex + direction, 0),
		families.length - 1
	);
	if (nextIndex === currentIndex) {
		return;
	}
	activeFamily.value = families[nextIndex];
	highlightedIndex.value = -1;
	// Keep DOM focus on the newly-active tab so the focus ring follows along.
	nextTick(focusActiveFamilyTab);
};

const selectHighlighted = () => {
	if (highlightedItemId.value == null) {
		return;
	}

	const input = scrollContent.value?.$el.querySelector(
		`[data-axis-item="${CSS.escape(String(highlightedItemId.value))}"] input`
	) as HTMLElement | null;
	input?.click();
};

// --- Region (column) navigation ---------------------------------------------
// Left/Right move between columns; Up/Down navigate within the active column.
type FocusRegion = "families" | "items" | "remove";

const removeButton = ref<{ $el: HTMLElement } | null>(null);
const focusRegion = ref<FocusRegion>("items");

const showRemoveButton = computed(() =>
	nullableAxis.value.includes(axisNameClean.value)
);

const focusRegions = computed<FocusRegion[]>(() => {
	// Family tabs are disabled while searching, so don't offer them as a column.
	const regions: FocusRegion[] = isSearching.value
		? ["items"]
		: ["families", "items"];
	if (showRemoveButton.value) {
		regions.push("remove");
	}
	return regions;
});

watch(isSearching, (searching) => {
	if (searching && focusRegion.value === "families") {
		focusRegion.value = "items";
	}
});

const focusActiveFamilyTab = () => {
	const column = familyColumn.value;
	if (!column) {
		return;
	}
	const tab =
		column.querySelector<HTMLElement>(".v-tab--selected") ??
		column.querySelector<HTMLElement>(".v-tab");
	tab?.focus();
};

const setRegion = (region: FocusRegion) => {
	focusRegion.value = region;
	nextTick(() => {
		if (region === "families") {
			focusActiveFamilyTab();
		} else if (region === "items") {
			searchField.value?.focus();
		} else if (region === "remove") {
			removeButton.value?.$el?.focus();
		}
	});
};

const moveRegion = (direction: 1 | -1) => {
	const regions = focusRegions.value;
	const currentIndex = regions.indexOf(focusRegion.value);
	const nextIndex = Math.min(
		Math.max(currentIndex + direction, 0),
		regions.length - 1
	);
	if (nextIndex !== currentIndex) {
		setRegion(regions[nextIndex]);
	}
};

const regionForElement = (element: Element | null): FocusRegion | null => {
	if (!element) {
		return null;
	}
	if (removeButton.value?.$el?.contains(element)) {
		return "remove";
	}
	if (familyColumn.value?.contains(element)) {
		return "families";
	}
	if (selectionRoot.value?.contains(element)) {
		return "items";
	}
	return null;
};

// Keyboard handling is scoped to this component's own columns. Focus anywhere
// else (sibling panels in the same dialog, dialog chrome, teleported
// menus/selects) is left entirely to native behaviour — including Tab.
const isWithinComponent = (element: Element | null): boolean => {
	if (!element || element === globalThis.document.body) {
		return true;
	}
	return (
		(familyColumn.value?.contains(element) ?? false) ||
		(selectionRoot.value?.contains(element) ?? false)
	);
};

// Keep the active column in sync when the user clicks into a region.
const handleFocusIn = (event: FocusEvent) => {
	const region = regionForElement(event.target as Element | null);
	if (region) {
		focusRegion.value = region;
	}
};

const navigationKeys = [
	"ArrowUp",
	"ArrowDown",
	"ArrowLeft",
	"ArrowRight",
	"Enter",
];

const handleKeyboardNavigation = (event: KeyboardEvent) => {
	const active = globalThis.document.activeElement;

	// Ignore keys while focus is outside this component (sibling panels, dialog
	// chrome, teleported menus/selects) so their native Tab/arrow handling wins.
	if (!isWithinComponent(active)) {
		return;
	}

	if (!navigationKeys.includes(event.key)) {
		return;
	}

	// If focus has drifted out of every region (e.g. onto dialog chrome or nothing),
	// pull it back into the current column so the keys keep working. We deliberately
	// do NOT copy the region from the focused element here — that is handled only on
	// real focus changes by handleFocusIn — so an explicit Left/Right move can never
	// be undone by the next keystroke.
	if (!regionForElement(active)) {
		setRegion(focusRegion.value);
	}

	switch (event.key) {
		case "ArrowLeft":
			event.preventDefault();
			event.stopImmediatePropagation();
			moveRegion(-1);
			return;
		case "ArrowRight":
			event.preventDefault();
			event.stopImmediatePropagation();
			moveRegion(1);
			return;
		case "ArrowDown":
			event.preventDefault();
			event.stopImmediatePropagation();
			if (focusRegion.value === "families") {
				moveHighlightedFamily(1);
			} else if (focusRegion.value === "items") {
				moveHighlight(1);
			}
			return;
		case "ArrowUp":
			event.preventDefault();
			event.stopImmediatePropagation();
			if (focusRegion.value === "families") {
				moveHighlightedFamily(-1);
			} else if (focusRegion.value === "items") {
				moveHighlight(-1);
			}
			return;
		case "Enter":
			if (focusRegion.value === "items" && highlightedItemId.value != null) {
				event.preventDefault();
				event.stopImmediatePropagation();
				selectHighlighted();
			} else if (focusRegion.value === "families") {
				event.preventDefault();
				event.stopImmediatePropagation();
				setRegion("items");
			} else if (focusRegion.value === "remove") {
				event.preventDefault();
				event.stopImmediatePropagation();
				resetAxis();
				setRegion("items");
			}
			return;
	}
};

onMounted(() => {
	// Focus the search field so the user can type right away when the dialog opens.
	// Wait a tick for the dialog transition to finish before focusing.
	nextTick(() => {
		searchField.value?.focus();
	});

	window.addEventListener("keydown", handleKeyboardNavigation, true);
	window.addEventListener("focusin", handleFocusIn, true);
});

onBeforeUnmount(() => {
	window.removeEventListener("keydown", handleKeyboardNavigation, true);
	window.removeEventListener("focusin", handleFocusIn, true);
});
</script>

<template>
	<div ref="familyColumn" class="d-flex flex-column axis-families">
		<div class="pa-2" v-if="itemDomains.length > 1 && isVertical">
			<v-select
				v-model="activeDomain"
				:items="itemDomains"
				:item-title="(domain) => ucfirst(t(`property_domains.${domain}.title`))"
				:item-value="(domain) => domain"
				density="compact"
				bg-color="secondary"
				hide-details
				prepend-inner-icon="fa: fa-regular fa-layer-group"
			/>
		</div>
		<perfect-scrollbar class="flex-grow-1" :options="{ suppressScrollX: true }">
			<v-tabs
				:key="familyTabsKey"
				center-active
				show-arrows
				hide-slider
				v-model="activeFamily"
				color="primary"
				direction="vertical"
				next-icon="mdi-chevron-down"
				prev-icon="mdi-chevron-up"
				bg-color="transparent"
			>
				<v-tab
					v-for="itemFamily in itemFamilies"
					:disabled="filterInput !== ''"
					:value="itemFamily"
					:key="itemFamily"
					class="family-tab"
					:class="{
						'region--focused':
							itemFamily === activeFamily && focusRegion === 'families',
					}"
				>
					<v-icon
						:icon="`fa: fa-regular ${t(
							`property_families.${itemFamily}.icon`
						)}`"
						size="12"
					/>
					<span class="family-label">
						{{ t(`property_families.${itemFamily}.title`) }}
					</span>
					<span
						v-if="selectMode === 'single'"
						v-show="familyWithActiveItem === itemFamily"
						class="family-dot"
					/>
					<span
						v-else-if="itemsWithFamily(itemFamily) > 0"
						class="family-count"
					>
						{{ itemsWithFamily(itemFamily) }}
					</span>
				</v-tab>
			</v-tabs>
		</perfect-scrollbar>
	</div>
	<div ref="selectionRoot" class="selection d-flex flex-column w-100 h-100">
		<div class="d-flex flex-row align-center ga-2 pa-2">
			<v-text-field
				ref="searchField"
				v-model="filterInput"
				:placeholder="t('axisDialog.search')"
				prepend-inner-icon="fa: fa-regular fa-magnifying-glass"
				hide-details
				density="compact"
				single-line
				clearable
				autocomplete="off"
				@click:clear="filterInput = ''"
			/>
			<v-btn
				ref="removeButton"
				v-if="showRemoveButton"
				variant="text"
				icon="fa: fa-regular fa-eye-slash"
				:rounded="false"
				color="primary"
				:disabled="!documentAttribute"
				:class="{ 'region--focused': focusRegion === 'remove' }"
				height="32"
				width="32"
				size="small"
				v-tooltip="t('axisDialog.removeSelection')"
				@click="resetAxis"
			/>
		</div>
		<v-divider />
		<perfect-scrollbar
			ref="scrollContent"
			class="flex-grow-1 pa-2"
			:options="{ suppressScrollX: true }"
		>
			<v-radio-group
				v-model="documentAttribute"
				column
				density="compact"
				v-if="selectMode === 'single'"
			>
				<template
					v-for="(visibleItems, groupName) in groupedVisibleItems"
					:key="groupName"
				>
					<div v-if="groupName" class="group-label">
						{{ t(`property_families.${groupName}.title`) }}
					</div>
					<selection-item
						v-for="visibleItem in visibleItems"
						v-model="documentAttribute"
						:key="visibleItem.id"
						:data-axis-item="visibleItem.id"
						:item="visibleItem"
						:show-description-as-text="isSearching"
						class="w-100"
						:class="{
							'axis-item--highlighted': visibleItem.id === highlightedItemId,
						}"
						:is-available="propertyIsAvailable(visibleItem)"
						:default-segment-id="undefined"
						:make-current-disabled="hasMultipleSelected"
					>
						<template
							v-if="
								documentAttribute?.id === visibleItem.id &&
								(visibleItem.type === 'hierarchy' ||
									visibleItem.type === 'bucketed' ||
									visibleItem.type === 'segment' ||
									verticalHasSegmentOption(visibleItem.name_clean))
							"
							v-slot:specification
						>
							<select-dimension-specification
								:dimensions="items"
								show-sort-direction
								:disable-sort-direction="isSortDirectionDisabled"
								:model-value="documentAttribute"
								@update:modelValue="updateDocumentAttribute"
							/>
						</template>
					</selection-item>
				</template>
			</v-radio-group>
			<div v-else>
				<template
					v-for="(visibleItems, groupName) in groupedVisibleItems"
					:key="groupName"
				>
					<div v-if="groupName" class="group-label">
						{{ t(`property_families.${groupName}.title`) }}
					</div>
					<checkbox-selection-item
						v-for="visibleItem in visibleItems"
						:key="visibleItem.id"
						v-model="documentAttribute"
						:data-axis-item="visibleItem.id"
						:item="visibleItem"
						:is-available="propertyIsAvailable(visibleItem)"
						:show-description-as-text="isSearching"
						class="w-100"
						:class="{
							'axis-item--highlighted': visibleItem.id === highlightedItemId,
						}"
					>
						<template
							v-if="
								documentAttribute.find((elem) => elem.id === visibleItem.id) &&
								(visibleItem.type === 'hierarchy' ||
									visibleItem.type === 'bucketed' ||
									visibleItem.type === 'segment' ||
									verticalHasSegmentOption(visibleItem.name_clean))
							"
							v-slot:specification
						>
							<select-dimension-specification
								:dimensions="items"
								:model-value="
									documentAttribute.find((elem) => elem.id === visibleItem.id)
								"
								@update:modelValue="updateDocumentAttribute"
							/>
						</template>
					</checkbox-selection-item>
				</template>
			</div>
		</perfect-scrollbar>
	</div>
</template>

<style lang="scss">
$label-font-size: 0.875rem;
$label-line-height: 1.2;
$row-height: 1.75rem;

.axis-families {
	flex: 0 0 17rem;
	width: 17rem;
	min-width: 0;
	background: rgb(var(--color-background));
	border-right: 1px solid rgba(var(--color-on-background), 0.2);

	.v-tab.family-tab {
		justify-content: stretch;
		width: 100%;
		padding: 0 0.6rem;
		text-transform: none;
		letter-spacing: normal;
		opacity: 1;
		height: $row-height;
		min-height: $row-height;

		.v-btn__content {
			justify-content: flex-start;
			gap: 0.4rem;
			width: 100%;
			min-width: 0;
		}

		&:hover {
			background: rgb(var(--color-secondary));
		}
	}

	.v-tab--selected.family-tab {
		background: rgb(var(--color-secondary));
		box-shadow: inset 2px 0 0 rgb(var(--color-primary));

		.family-label {
			font-weight: 600;
		}
	}
}

.family-label {
	flex: 1 1 auto;
	text-align: start;
	font-size: $label-font-size;
	line-height: $label-line-height;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.family-dot {
	display: block;
	width: 0.375rem;
	height: 0.375rem;
	border-radius: 50%;
	background: rgb(var(--color-primary));
}

.family-count {
	font-size: 0.75rem;
	line-height: 1;
	color: rgb(var(--color-on-background));
}

.selection {
	min-width: 0;

	.group-label {
		font-size: 0.75rem;
		font-weight: 600;
		line-height: 1.4;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: rgb(var(--color-on-background));
		border-bottom: 1px solid rgba(var(--color-on-background), 0.2);
		margin-bottom: 0.25rem;
	}

	.axis-item > .v-input {
		flex: 1 1 auto;
		min-width: 0;
	}

	.v-selection-control {
		--v-selection-control-size: 1.25rem;
		flex: 1 1 auto;
		min-height: 2rem;
		min-width: 0;
		padding-inline-start: 0.25rem;

		.v-selection-control__input .v-icon {
			font-size: 1rem;
		}

		.v-label {
			flex: 1 1 auto;
			min-width: 0;
			font-size: $label-font-size;
			line-height: $label-line-height;
			opacity: 1;
		}
	}

	.axis-item__specification {
		width: 15rem;

		.v-input {
			--v-input-control-height: 2rem;
		}

		.v-field__input {
			min-height: 2rem;
			padding-top: 0;
			padding-bottom: 0;
			font-size: 0.8125rem;
		}
	}
}

.axis-item--highlighted {
	background-color: rgba(var(--color-primary), 0.08);
	border-radius: 6px;
}

.v-tab.region--focused,
.v-btn.region--focused {
	background-color: rgba(var(--color-primary), 0.08) !important;
	border-radius: 6px;
}
</style>
